import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowRight, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/lib/shadcn/components/ui/dialog';
import { Button } from '@/lib/shadcn/components/ui/button';
import { Checkbox } from '@/lib/shadcn/components/ui/checkbox';
import {
  getBexioMigrationErrorMessage,
  useBexioContactMigration,
} from '../../queries/bexioContactMigration.query';
import {
  BEXIO_CONTACT_TYPE,
  type BexioContactMigrationEntry,
  type BexioContactMigrationResult,
} from '../../models/bexioContactMigration.model';
import {
  bexioEntryKey,
  formatCompanyName,
  formatDuplicateName,
  formatPersonName,
  getConvertibleEntries,
  getDuplicateEntries,
  getEntriesWithStatus,
} from '../../utils/bexioContactMigration.utils';

export type BexioContactMigrationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Step = 'intro' | 'review' | 'done';

const T = 'bexioMigration';

export const BexioContactMigrationDialog: React.FC<
  BexioContactMigrationDialogProps
> = ({ open, onOpenChange }) => {
  const { t } = useTranslation('customers');
  const migration = useBexioContactMigration();
  const [step, setStep] = useState<Step>('intro');
  const [dryRun, setDryRun] = useState<BexioContactMigrationResult | null>(null);
  const [runResult, setRunResult] = useState<BexioContactMigrationResult | null>(null);
  /** Keys (tenant + Bexio id) of contacts the user checked to keep as company. */
  const [excludedKeys, setExcludedKeys] = useState<Set<string>>(new Set());

  const { reset: resetMigration } = migration;
  useEffect(() => {
    if (open) {
      setStep('intro');
      setDryRun(null);
      setRunResult(null);
      setExcludedKeys(new Set());
      resetMigration();
    }
  }, [open, resetMigration]);

  const convertible = useMemo(
    () => getConvertibleEntries(dryRun?.entries ?? []),
    [dryRun]
  );
  const duplicates = useMemo(
    () => getDuplicateEntries(dryRun?.entries ?? []),
    [dryRun]
  );
  const notConvertible = useMemo(
    () => getEntriesWithStatus(dryRun?.entries ?? [], 'skipped', 'failed'),
    [dryRun]
  );
  const toConvertCount = convertible.filter(
    entry => !excludedKeys.has(bexioEntryKey(entry))
  ).length;
  const allExcluded =
    convertible.length > 0 && excludedKeys.size === convertible.length;

  const isRunning = migration.isPending;
  const errorMessage = getBexioMigrationErrorMessage(migration.error);

  const handleOpenChange = (next: boolean) => {
    // The backend keeps working if the dialog closes mid-request; keep it open so the result is not lost.
    if (!next && isRunning) return;
    onOpenChange(next);
  };

  const startDryRun = async () => {
    try {
      const result = await migration.mutateAsync({ dryRun: true });
      setDryRun(result);
      setExcludedKeys(new Set());
      setStep('review');
    } catch {
      // Shown via migration.error
    }
  };

  const startConversion = async () => {
    const excludedContacts = convertible
      .filter(entry => excludedKeys.has(bexioEntryKey(entry)))
      .map(({ tenantId, bexioId }) => ({ tenantId, bexioId }));
    try {
      const result = await migration.mutateAsync({
        dryRun: false,
        excludedContacts,
      });
      setRunResult(result);
      setStep('done');
    } catch {
      // Shown via migration.error
    }
  };

  const toggleExcluded = (entry: BexioContactMigrationEntry, checked: boolean) => {
    setExcludedKeys(prev => {
      const next = new Set(prev);
      if (checked) next.add(bexioEntryKey(entry));
      else next.delete(bexioEntryKey(entry));
      return next;
    });
  };

  const toggleAllExcluded = (checked: boolean) => {
    setExcludedKeys(checked ? new Set(convertible.map(bexioEntryKey)) : new Set());
  };

  const renderEntryNames = (entry: BexioContactMigrationEntry) => (
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-x-2 text-sm">
        <span className="text-muted-foreground line-through decoration-muted-foreground/60">
          {formatCompanyName(entry) || '—'}
        </span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="font-medium">{formatPersonName(entry) || '—'}</span>
      </div>
      <div className="truncate text-xs text-muted-foreground">
        {entry.email || '—'} · {t(`${T}.bexioId`, { id: entry.bexioId })}
      </div>
    </div>
  );

  const renderInfoList = (
    entries: BexioContactMigrationEntry[],
    title: string,
    testId: string
  ) =>
    entries.length > 0 && (
      <div data-testid={testId} className="space-y-2">
        <h4 className="text-sm font-medium">
          {title} ({entries.length})
        </h4>
        <ul className="divide-y rounded-md border">
          {entries.map(entry => (
            <li key={bexioEntryKey(entry)} className="px-3 py-2">
              {renderEntryNames(entry)}
              {entry.reason && (
                <div className="mt-1 text-xs text-destructive">{entry.reason}</div>
              )}
            </li>
          ))}
        </ul>
      </div>
    );

  const renderIntro = () => (
    <div className="space-y-3 text-sm">
      <p>{t(`${T}.introText`)}</p>
      <p className="text-muted-foreground">{t(`${T}.introDryRunHint`)}</p>
    </div>
  );

  const renderReview = () => {
    if (!dryRun) return null;
    return (
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          {t(`${T}.reviewSummary`, {
            checked: dryRun.checked,
            companies: dryRun.entries.length,
          })}
        </p>

        {convertible.length === 0 ? (
          <p data-testid="bexio-migration-nothing-to-do" className="text-sm font-medium">
            {t(`${T}.nothingToConvert`)}
          </p>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <h4 className="text-sm font-medium">{t(`${T}.convertibleTitle`)}</h4>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={allExcluded}
                  onCheckedChange={toggleAllExcluded}
                  disabled={isRunning}
                  aria-label={t(`${T}.excludeAll`)}
                />
                {t(`${T}.excludeAll`)}
              </label>
            </div>
            <p className="text-xs text-muted-foreground">{t(`${T}.excludeHint`)}</p>
            <ul data-testid="bexio-migration-convertible" className="divide-y rounded-md border">
              {convertible.map(entry => {
                const key = bexioEntryKey(entry);
                const excluded = excludedKeys.has(key);
                return (
                  <li
                    key={key}
                    data-testid={`bexio-migration-entry-${entry.bexioId}`}
                    className={`flex items-center gap-3 px-3 py-2 ${excluded ? 'bg-muted/60' : ''}`}
                  >
                    <Checkbox
                      checked={excluded}
                      onCheckedChange={checked => toggleExcluded(entry, checked)}
                      disabled={isRunning}
                      aria-label={t(`${T}.excludeContact`, {
                        name: formatPersonName(entry) || entry.bexioId,
                      })}
                    />
                    {renderEntryNames(entry)}
                    {excluded && (
                      <span className="shrink-0 text-xs font-medium text-muted-foreground">
                        {t(`${T}.excludedBadge`)}
                      </span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {renderDuplicates()}

        {renderInfoList(notConvertible, t(`${T}.notConvertibleTitle`), 'bexio-migration-not-convertible')}
      </div>
    );
  };

  /** Automatically excluded: converting would leave two persons with the same email. Not selectable for now. */
  const renderDuplicates = () =>
    duplicates.length > 0 && (
      <div data-testid="bexio-migration-duplicates" className="space-y-2">
        <h4 className="text-sm font-medium">
          {t(`${T}.duplicatesTitle`)} ({duplicates.length})
        </h4>
        <p className="text-xs text-muted-foreground">{t(`${T}.duplicatesHint`)}</p>
        <ul className="divide-y rounded-md border">
          {duplicates.map(entry => (
            <li
              key={bexioEntryKey(entry)}
              data-testid={`bexio-migration-duplicate-${entry.bexioId}`}
              aria-disabled="true"
              className="flex items-start gap-3 bg-muted/40 px-3 py-2 opacity-60"
            >
              <Checkbox
                checked
                disabled
                className="mt-0.5"
                aria-label={t(`${T}.excludeContact`, {
                  name: formatPersonName(entry) || entry.bexioId,
                })}
              />
              <div className="min-w-0 flex-1">
                {renderEntryNames(entry)}
                {(entry.duplicateContacts ?? []).length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    {t(`${T}.duplicateOf`)}{' '}
                    {(entry.duplicateContacts ?? [])
                      .map(other =>
                        [
                          formatDuplicateName(other) || '—',
                          other.contactTypeId === BEXIO_CONTACT_TYPE.PERSON
                            ? t(`${T}.contactTypePerson`)
                            : t(`${T}.contactTypeCompany`),
                          t(`${T}.bexioId`, { id: other.bexioId }),
                        ].join(' · ')
                      )
                      .join('; ')}
                  </div>
                )}
              </div>
              <span className="shrink-0 text-xs font-medium text-muted-foreground">
                {t(`${T}.duplicateBadge`)}
              </span>
            </li>
          ))}
        </ul>
      </div>
    );

  const renderDone = () => {
    if (!runResult) return null;
    const failed = getEntriesWithStatus(runResult.entries, 'failed');
    return (
      <div className="space-y-4">
        <ul data-testid="bexio-migration-run-summary" className="space-y-1 text-sm">
          <li>{t(`${T}.convertedCount`, { count: runResult.converted })}</li>
          <li>{t(`${T}.skippedCount`, { count: runResult.skipped })}</li>
          {(runResult.duplicates ?? 0) > 0 && (
            <li>{t(`${T}.duplicatesCount`, { count: runResult.duplicates })}</li>
          )}
          <li className={runResult.failed > 0 ? 'font-medium text-destructive' : ''}>
            {t(`${T}.failedCount`, { count: runResult.failed })}
          </li>
        </ul>
        {runResult.failed > 0 && (
          <p className="text-sm text-muted-foreground">{t(`${T}.failedHint`)}</p>
        )}
        {renderInfoList(failed, t(`${T}.failedTitle`), 'bexio-migration-failed')}
      </div>
    );
  };

  const renderFooter = () => {
    if (step === 'intro') {
      return (
        <>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isRunning}>
            {t(`${T}.cancel`)}
          </Button>
          <Button type="button" onClick={startDryRun} disabled={isRunning}>
            {isRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRunning ? t(`${T}.dryRunRunning`) : t(`${T}.startDryRun`)}
          </Button>
        </>
      );
    }
    if (step === 'review') {
      return (
        <>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)} disabled={isRunning}>
            {t(`${T}.cancel`)}
          </Button>
          <Button type="button" variant="outline" onClick={startDryRun} disabled={isRunning}>
            {t(`${T}.repeatDryRun`)}
          </Button>
          <Button
            type="button"
            onClick={startConversion}
            disabled={isRunning || toConvertCount === 0}
          >
            {isRunning && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isRunning
              ? t(`${T}.converting`)
              : t(`${T}.convertButton`, { count: toConvertCount })}
          </Button>
        </>
      );
    }
    return (
      <Button type="button" onClick={() => handleOpenChange(false)}>
        {t(`${T}.close`)}
      </Button>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="flex max-h-[90vh] flex-col sm:max-w-2xl"
        data-testid="bexio-migration-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t(`${T}.title`)}</DialogTitle>
          <DialogDescription>
            {step === 'done' ? t(`${T}.doneDescription`) : t(`${T}.description`)}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto pr-1">
          {step === 'intro' && renderIntro()}
          {step === 'review' && renderReview()}
          {step === 'done' && renderDone()}
        </div>

        {errorMessage && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {t(`${T}.error`)} {errorMessage}
            </span>
          </div>
        )}

        <DialogFooter className="gap-2">{renderFooter()}</DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BexioContactMigrationDialog;
